<div class="fixed inset-0 z-40 flex">
  <div class="flex-1 bg-black/30" (click)="closePanel.emit()" aria-hidden="true"></div>

  <aside
    class="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto border-l border-gray-200 flex flex-col"
    appModalDialog
    (dialogClose)="closePanel.emit()"
    role="dialog"
    aria-modal="true"
    aria-label="AI Evaluation"
  >
    <!-- Header -->
    <div class="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
      <div class="flex items-center gap-2">
        <i class="ri-sparkling-line text-primary text-lg"></i>
        <h2 class="text-base font-semibold text-gray-800">AI Evaluation</h2>
        @if (evaluation) {
          <span class="text-xs text-gray-500">
            {{ evaluation.model }} · {{ evaluation.prompt_version }}
          </span>
        }
      </div>
      <div class="flex items-center gap-2">
        @if (evaluation && !loading) {
          <button
            class="btn btn-xs btn-ghost"
            (click)="reevaluate.emit()"
            title="Re-evaluate (force fresh call)"
          >
            <i class="ri-refresh-line"></i>
            Re-evaluate
          </button>
        }
        <button class="btn btn-xs btn-ghost" (click)="closePanel.emit()" aria-label="Close">
          <i class="ri-close-line text-lg"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 p-4 space-y-4">
      @if (loading) {
        <div class="relative min-h-[200px]">
          <ngx-spinner
            size="medium"
            color="#cd0005"
            bdColor="transparent"
            [fullScreen]="false"
          ></ngx-spinner>
          <div class="absolute inset-0 flex items-end justify-center pb-4">
            <span class="text-xs text-gray-500">This can take up to 30s…</span>
          </div>
        </div>
      } @else if (error) {
        <div role="alert" class="alert alert-error text-sm">
          <i class="ri-error-warning-line"></i>
          <span>{{ error }}</span>
        </div>
      } @else if (result) {
        <!-- Summary + outcome -->
        <div class="flex flex-wrap items-start gap-3">
          <div
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
            [ngClass]="outcomeBadgeClasses(result.outcome)"
          >
            {{ result.outcome | uppercase }}
          </div>
          <div
            class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
          >
            {{ result.flow_detected }}
          </div>
          @for (p of result.providers_involved; track p) {
            <div
              class="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-700"
            >
              {{ p }}
            </div>
          }
        </div>
        <p class="text-sm text-gray-800 leading-relaxed">{{ result.summary }}</p>

        <!-- Timeline -->
        @if (result.timeline?.length) {
          <div>
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Timeline</h3>
            <ol class="space-y-1.5">
              @for (step of result.timeline; track trackStep($index, step)) {
                <li class="flex items-start gap-2 text-sm">
                  <span
                    class="inline-block w-2 h-2 rounded-full mt-1.5 shrink-0"
                    [ngClass]="statusDotClasses(step.status)"
                  ></span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-baseline gap-2">
                      <span class="font-medium text-gray-800">{{ step.step }}</span>
                      @if (step.ts) {
                        <span class="text-xs text-gray-500">{{ step.ts }}</span>
                      }
                      <span class="text-xs text-gray-500">· {{ step.status }}</span>
                    </div>
                    @if (step.note) {
                      <div class="text-xs text-gray-600 mt-0.5">{{ step.note }}</div>
                    }
                  </div>
                </li>
              }
            </ol>
          </div>
        }

        <!-- Issues -->
        @if (result.issues?.length) {
          <div>
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Issues</h3>
            <ul class="space-y-2">
              @for (issue of result.issues; track trackIssue($index, issue)) {
                <li class="rounded-md border p-2" [ngClass]="severityBadgeClasses(issue.severity)">
                  <div class="flex items-baseline gap-2">
                    <span class="text-xs font-semibold uppercase">{{ issue.severity }}</span>
                    <span class="text-xs">{{ issue.where }}</span>
                  </div>
                  <div class="text-sm mt-0.5">{{ issue.what }}</div>
                  @if (issue.root_cause) {
                    <div class="text-xs mt-1 opacity-80">
                      <em>Root cause:</em>
                      {{ issue.root_cause }}
                    </div>
                  }
                </li>
              }
            </ul>
          </div>
        }

        <!-- Recommendations -->
        @if (result.recommendations?.length) {
          <div>
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Recommendations</h3>
            <ul class="list-disc pl-5 space-y-1 text-sm text-gray-800">
              @for (rec of result.recommendations; track rec) {
                <li>{{ rec }}</li>
              }
            </ul>
          </div>
        }

        <!-- Telemetry footer -->
        @if (evaluation?.latency_ms || evaluation?.input_tokens) {
          <div class="pt-3 mt-3 border-t border-gray-200 text-xs text-gray-500 flex gap-3">
            @if (evaluation?.latency_ms) {
              <span>{{ evaluation?.latency_ms }} ms</span>
            }
            @if (evaluation?.input_tokens) {
              <span>
                {{ evaluation?.input_tokens }} in · {{ evaluation?.output_tokens }} out tokens
              </span>
            }
          </div>
        }
      } @else {
        <div class="text-sm text-gray-500">No evaluation yet.</div>
      }
    </div>
  </aside>
</div>
